import styled from "styled-components";

export const CalendarContainer = styled.div`
  /* Keep calendar and the Unscheduled panel the same height */
  height: ${({ view }) => (view === "month" ? "740px" : "555px")};
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0; /* critical: prevent children with overflow from blowing out the grid */
  width: 100%;
`;

export const DateCellWrapper = styled.div`
  height: 100%;
  width: 100%;
  /* Month view: always show vertical cell borders (left/right) like Figma. */
  border-left: ${({ variant, isToday }) =>
    variant === "month" ? (isToday ? "none" : "1px solid #D3E0FC") : "none"};
  border-right: ${({ variant, isToday }) =>
    variant === "month" ? (isToday ? "none" : "1px solid #D3E0FC") : "none"};

  /* Gradient border for today's date in month view */
  ${({ variant, isToday }) =>
    variant === "month" && isToday
      ? `
    border: 1px solid;
    border-image: linear-gradient(135deg, #2266EF 0%, #6C2EF2 100%) 1;
  `
      : ""}

  /* Keep the previous drag/drop hover indicator (dashed) via outline, so it doesn't wipe the month borders. */
  outline: ${({ isOver, theme, isToday }) =>
    isOver && !isToday
      ? `2px dashed ${theme.colors.main.primary || "#2268EF"}`
      : "none"};
  outline-offset: ${({ isOver, isToday }) =>
    isOver && !isToday ? "-2px" : "0"};

  border-radius: ${({ variant, isOver }) =>
    variant === "month" ? "0" : isOver ? "4px" : "0"};
  background: ${({ isOver, theme }) =>
    isOver ? theme.colors.main.secondary || "#f0f0f0" : "transparent"};
  transition: all 0.2s ease;
  position: relative;
`;

export const TimeSlotWrapper = styled.div`
  height: 100%;
  width: 100%;
  border-radius: ${({ isOver }) => (isOver ? "4px" : "0")};
  border: ${({ isOver, theme }) =>
    isOver ? `2px dashed ${theme.colors.main.primary || "#2268EF"}` : "none"};
  background: ${({ isOver, theme }) =>
    isOver ? theme.colors.main.secondary || "#f0f0f0" : "transparent"};
  transition: all 0.2s ease;
  position: relative;
`;

export const TimeSlotCellWrapper = styled.div`
  height: 100%;
  width: 100%;
  border-radius: ${({ isOver }) => (isOver ? "4px" : "0")};
  border: ${({ isOver, theme }) =>
    isOver ? `2px dashed ${theme.colors.main.primary || "#2268EF"}` : "none"};
  background: ${({ isOver, theme }) =>
    isOver ? theme.colors.main.secondary || "#f0f0f0" : "transparent"};
  transition: all 0.2s ease;
  position: relative;
`;

export const CalendarToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

export const MonthPickerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const NextPrevButton = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.colors.main.white || "#FFFFFF"};
  border: 1px solid
    ${({ theme }) => theme.colors.border.secondaryLight || "#999FAA"};
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.main.secondary || "#f0f0f0"};
  }
`;

export const MonthChangeIcon = styled.img`
  width: 16px;
  height: 16px;
`;

export const MonthPickerInput = styled.div`
  margin: 0px 15px;
  font-size: 13px;
  text-align: center;
  border: none;
  &:focus {
    outline: none;
  }
`;

export const ViewButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ gap }) => gap || "5px"};
`;

export const ViewButton = styled.div`
  display: flex;
  align-self: center;
  border-radius: ${({ theme }) => theme.button.borderRadius};
  border: ${({ theme }) => `1px solid ${theme.button.primary.bgColor}`};
  background-color: ${({ active, theme }) =>
    active ? theme.button.primary.bgColor : theme.colors.main.white};
  font-size: ${({ theme }) => theme.button.fontSize};
  color: ${({ active, theme }) =>
    active ? theme.colors.main.white : theme.button.primary.bgColor};
  padding: 6px 18px;
  cursor: pointer;
  /* &:hover {
    background-color: ${({ active }) => !active && `#eaf0f5`};
  } */
`;

export const CalendarIconButton = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  min-width: 80px;
  padding: 10px 8px;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme }) => theme.colors.border.secondaryLight || "#999FAA"};
  background: ${({ theme }) => theme.colors.main.white || "#FFFFFF"};
  cursor: pointer;
  box-sizing: border-box;
`;

export const CalendarIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

export const CalendarButtonText = styled.span`
  font-size: ${({ theme, fontSize }) =>
    fontSize || theme.font.pageText.size || "14px"};
  color: ${({ theme }) => theme.font.pageText.colorSecondary || "#353A46"};
  white-space: nowrap;
`;

// Collapsed "Unscheduled" vertical tab (Figma-like)
export const UnscheduledCollapsedTab = styled.button`
  width: 36px;
  /* Match CalendarContainer height */
  height: ${({ view }) => (view === "month" ? "740px" : "555px")};
  border-radius: 8px;
  border: 1px solid #b4c6f9;
  background: ${({ theme }) => theme.colors.main.white || "#FFFFFF"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 8px 4px;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.2s ease;
`;

export const UnscheduledCollapsedText = styled.span`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 14px;
  font-weight: 600;
  font-family: ${({ theme }) =>
    theme.defaultFontFamily || '"Mona Sans", sans-serif'};
  color: #000e2a;
  letter-spacing: 0;
  line-height: 19.74px;
`;

export const UnscheduledCountBadge = styled.span`
  width: 20px;
  height: 22px;
  border-radius: 32px;
  background: #353a46;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  font-family: ${({ theme }) =>
    theme.defaultFontFamily || '"Mona Sans", sans-serif'};
  flex-shrink: 0;
  line-height: 14px;
`;

export const UnscheduledToggleIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

export const CalendarLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  height: 25px;
  border: 1px solid ${({ borderColor }) => borderColor || "#E1E5E8"};
  border-radius: 4px;
  background: ${({ bgColor }) => bgColor || "#FFFFFF"};
  font-size: 12px;
  font-weight: 600;
  font-family: ${({ theme }) =>
    theme.defaultFontFamily || '"Mona Sans", sans-serif'};
  color: #000e2a;
  line-height: 16.92px;
`;

export const LegendDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color || "#2266EF"};
  flex-shrink: 0;
`;

export const LegendSquare = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${({ color }) => color || "#6C2EF2"};
  flex-shrink: 0;
`;
